import { useLazyQuery } from '@apollo/client';
import { ClickAwayListener, Popper, TextField } from '@mui/material';
import { gql } from '__generated__';
import { DesignationType } from '__generated__/graphql';
import { FC, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import debounce from 'utils/debounce';

import theme from './theme.module.scss';

type Designation = {
  id: string;
  name: string;
};

const SEARCH_QUERY = gql(`
  query SearchDesignations($filters: DesignationFilterInputType, $limit: Int) {
    designations(filters: $filters, limit: $limit) {
      nodes {
        id
        name
      }
      pageInfo {
        cursor
        totalCount
      }
    }
  }
`);

const SearchDesignations: FC<{
  onChange: (designation: Designation) => void;
  value?: Designation;
  label?: string;
  readMode?: boolean;
}> = ({ value, onChange, label = '', readMode }) => {
  const [currentValue, setCurrentValue] = useState<Designation | null>(value || null);
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };
  const [getDesignations, { loading, error, data, refetch, variables }] = useLazyQuery(
    SEARCH_QUERY,
    {
      fetchPolicy: 'network-only',
    }
  );

  const ref = useRef<HTMLDivElement | null>(null);

  const performSearch = searchTerm => {
    if (!data) {
      getDesignations({
        variables: {
          filters: {
            departmentId,
            text: searchTerm.trim(),
          },
          limit: 8,
        },
      });
      return;
    }
    refetch({
      filters: {
        departmentId,
        text: searchTerm.trim(),
      },
    });
  };

  function results() {
    if (loading) return <LoadingIndicator />;

    if (error) return <ErrorMessage error={error} refetch={refetch} />;

    if (!data || !data.designations || variables?.filters?.text === '') return null;

    const designations = data.designations.nodes;

    if (designations.length === 0) return <p>No results found. Please change your search.</p>;

    return (
      <>
        {designations.map(designation => (
          <div
            key={designation.id}
            className={theme.designation}
            onClick={() => {
              setCurrentValue(designation);
              onChange(designation as DesignationType);
            }}
          >
            <span className={theme.name}>{designation.name}</span>
          </div>
        ))}
      </>
    );
  }

  if (currentValue) {
    return (
      <>
        <p>{currentValue.name}</p>
        {readMode ? null : <Button onClick={() => setCurrentValue(null)}>Clear</Button>}
      </>
    );
  }

  return (
    <div className={theme.container}>
      <ClickAwayListener onClickAway={() => performSearch('')}>
        <div className={theme.inputContainer}>
          <TextField
            ref={ref}
            className={theme.input}
            fullWidth
            size="small"
            label={label}
            onBlur={e => (e.target.value = '')}
            onChange={debounce(e => performSearch(e.target.value), 500)}
            placeholder="Find by name"
          />
          <Popper
            open={true}
            style={{ width: ref.current?.offsetWidth, zIndex: 10000 }}
            anchorEl={ref.current}
            placement="bottom-start"
            onClick={e => e.preventDefault()}
          >
            <div className={theme.resultsContainer}>{results()}</div>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default SearchDesignations;
